import axios from 'axios';
import type {
  AudioUploadResult,
  AuthResult,
  ConvExchange,
  ProjectDevelopContext,
  StoryDocument,
  StoryProject,
} from '@/types/api';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '';
/** Axios instance — credentials for httpOnly session cookie; Bearer set per-request. */
export const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

export function setAuthToken(token: string | null): void {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
}

export async function register(params: {
  email: string;
  password: string;
  displayName?: string;
}): Promise<AuthResult> {
  const { data } = await api.post<AuthResult>('/api/auth/register', params);
  return data;
}

export async function login(params: {
  email: string;
  password: string;
}): Promise<AuthResult> {
  const { data } = await api.post<AuthResult>('/api/auth/login', params);
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/api/auth/logout');
}

/** Restore web session from httpOnly cookie (page refresh). */
export async function fetchSession(): Promise<AuthResult> {
  const { data } = await api.get<AuthResult>('/api/auth/session');
  return data;
}

export async function fetchProjects(): Promise<StoryProject[]> {
  const { data } = await api.get<StoryProject[]>('/api/projects');
  return data;
}

export async function createProject(params: {
  title: string;
  format?: string;
  genre?: string;
}): Promise<StoryProject> {
  const { data } = await api.post<StoryProject>('/api/projects', params);
  return data;
}

export async function fetchTimeline(limit = 50): Promise<ConvExchange[]> {
  const { data } = await api.get<ConvExchange[]>('/api/exchanges/timeline', {
    params: { limit },
  });
  return data;
}

export async function submitMessage(params: {
  message?: string;
  audioFileUrl?: string;
  projectId?: string;
}): Promise<ConvExchange> {
  const { data } = await api.post<ConvExchange>('/api/exchanges', params);
  return data;
}

export async function uploadAudio(blob: Blob, filename: string): Promise<AudioUploadResult> {
  const form = new FormData();
  form.append('audio', new File([blob], filename, { type: blob.type || 'audio/webm' }));
  const { data } = await api.post<AudioUploadResult>('/api/audio/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function retryExchange(id: string): Promise<ConvExchange> {
  const { data } = await api.post<ConvExchange>(`/api/exchanges/${id}/retry`);
  return data;
}

export function exchangeAudioDownloadUrl(id: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? window.location.origin;
  return `${base}/api/exchanges/${id}/audio`;
}

export async function downloadExchangeAudio(id: string): Promise<Blob> {
  const { data } = await api.get<Blob>(`/api/exchanges/${id}/audio`, {
    responseType: 'blob',
  });
  return data;
}

export async function fetchExchange(id: string): Promise<ConvExchange> {
  const { data } = await api.get<ConvExchange>(`/api/exchanges/${id}`);
  return data;
}

export async function fetchDevelopContext(
  projectId: string,
): Promise<ProjectDevelopContext> {
  const { data } = await api.get<ProjectDevelopContext>(
    `/api/projects/${projectId}/develop`,
  );
  return data;
}

export async function fetchProjectDocument(
  projectId: string,
  kind: string,
): Promise<StoryDocument> {
  const { data } = await api.get<StoryDocument>(
    `/api/projects/${projectId}/documents/${kind}`,
  );
  return data;
}
